import type { RefObject } from 'react';
import { NavLink } from 'react-router-dom';

import { useDocumentFlag } from '../../shared/hooks/useDocumentFlag';

import s from './BottomTabBar.module.css';
import type { NavTabs } from './nav-types';
import { NavIcon } from './NavIcon';

interface BottomTabBarProps {
  tabs: NavTabs;
  /** Mirrors the drawer; the More button reports it via aria-expanded. */
  moreOpen: boolean;
  onMore: () => void;
  /** AppShell focuses this when a drawer opened from More closes. */
  moreButtonRef: RefObject<HTMLButtonElement>;
}

/**
 * The phone's primary navigation: four destinations and More.
 *
 * Its own landmark ("Quick navigation"), distinct from the drawer's "Primary",
 * because the two overlap on purpose — the bar is the most-used four, the
 * drawer is everything. It never hides on scroll: a landmark that moves under
 * a screen-reader or keyboard user is a bug, and iOS's own tab bars never do.
 * Mounting it flips `--bottom-nav-offset` for every fixed overlay in the app.
 */
export function BottomTabBar({
  tabs,
  moreOpen,
  onMore,
  moreButtonRef,
}: BottomTabBarProps): JSX.Element {
  useDocumentFlag('data-bottom-nav', true);

  return (
    <nav className={s.root} aria-label="Quick navigation">
      <ul className={s.list}>
        {tabs.map((tab) => (
          <li key={tab.to} className={s.item}>
            <NavLink
              to={tab.to}
              end={tab.matchSubRoutes !== true}
              viewTransition
              className={({ isActive }) => (isActive ? `${s.tab} ${s.tabActive}` : (s.tab ?? ''))}
            >
              <span className={s.icon}>
                <NavIcon name={tab.icon} />
              </span>
              <span className={s.label}>{tab.label}</span>
            </NavLink>
          </li>
        ))}
        <li className={s.item}>
          <button
            ref={moreButtonRef}
            type="button"
            className={moreOpen ? `${s.tab} ${s.tabActive}` : s.tab}
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            onClick={onMore}
          >
            <span className={s.icon}>
              <NavIcon name="more" />
            </span>
            <span className={s.label}>More</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}
